import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import FormSelect from "../../questionnaire/components/FormSelect";
import AppButton from "../../questionnaire/components/AppButton";
import CheckinLayout from "../../questionnaire/components/CheckinLayout";
import { useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useDemographics } from "../../questionnaire/contexts/DemographicsContext";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import { useLoader } from "../../questionnaire/contexts/LoaderContext";
import { Alert } from "../../components/shared/Alert";

const phoneTypeOptions = [
  { label: "Mobile", value: "mobile" },
  { label: "Home", value: "home" },
  { label: "Work", value: "work" },
];

const tableColumns = ["Email", "Phone", "SMS"];

const ContactAndCommunication: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { demographics, loading, error, updateDemographics } = useDemographics();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError, loading: workflowLoading, refreshWorkflow } = useWorkflow();
  const { show, hide, withLoader } = useLoader();

  React.useEffect(() => {
    // Hide the loader as soon as an error is reported — otherwise the
    // overlay covers the error Alert and the user sees an infinite spinner
    // (e.g. PAST_APPOINTMENT keeps `loading=false` but `demographics=null`).
    const hasError = !!(error || workflowError);
    const initialLoading = (workflowLoading || loading || !demographics) && !hasError;
    if (initialLoading) show("Loading checkin...");
    else hide();
    return () => hide();
  }, [workflowLoading, loading, demographics, error, workflowError, show, hide]);

  const [submitting, setSubmitting] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const inFlightSubmitRef = React.useRef(false);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [phoneError, setPhoneError] = React.useState<string | null>(null);

  const contact = demographics?.contactInfo;
  const commPrefs = demographics?.communicationPreference;

  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [phoneType, setPhoneType] = React.useState<"mobile" | "home" | "work">("mobile");

  // Store initial state for change detection
  const [initialState, setInitialState] = React.useState<{
    email: string;
    mobilePhone: string;
    homePhone: string;
    workPhone: string;
    communicationPreference: typeof commPrefs;
  } | null>(null);

  // Helper function to get phone number based on type
  const getPhoneByType = (type: "mobile" | "home" | "work", contactInfo: typeof contact): string => {
    if (!contactInfo) return "";
    switch (type) {
      case "mobile":
        return contactInfo.mobilePhone || "";
      case "home":
        return contactInfo.homePhone || "";
      case "work":
        return contactInfo.workPhone || "";
      default:
        return "";
    }
  };

  // Initialize phone type and phone number from demographics
  React.useEffect(() => {
    if (contact) {
      const initialEmail = contact.email || "";
      const initialMobilePhone = contact.mobilePhone || "";
      const initialHomePhone = contact.homePhone || "";
      const initialWorkPhone = contact.workPhone || "";
      
      setEmail(initialEmail);
      
      // Determine initial phone type: prefer mobile, then home, then work
      let initialType: "mobile" | "home" | "work" = "mobile";
      if (initialMobilePhone) {
        initialType = "mobile";
      } else if (initialHomePhone) {
        initialType = "home";
      } else if (initialWorkPhone) {
        initialType = "work";
      }
      
      setPhoneType(initialType);
      setPhone(getPhoneByType(initialType, contact));
      
      // Store initial state for change detection
      setInitialState({
        email: initialEmail,
        mobilePhone: initialMobilePhone,
        homePhone: initialHomePhone,
        workPhone: initialWorkPhone,
        communicationPreference: commPrefs || undefined,
      });
    }
  }, [contact, commPrefs]);

  const validateEmail = (value: string): string | null => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!value) return "Email address is required";
    if (!emailRegex.test(value)) return "Please enter a valid email address (e.g., user@example.com)";
    return null;
  };

  const validatePhone = (value: string): string | null => {
    const digitsOnly = value.replace(/\D/g, "");
    if (!value) return "Phone number is required";
    if (digitsOnly.length !== 10) return "Please enter a valid 10-digit phone number (e.g., 4155550067)";
    return null;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(value);
    setPhoneError(validatePhone(value));
  };

  const handlePhoneTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as "mobile" | "home" | "work";
    setPhoneType(newType);
    // Update phone number to show the phone for the selected type
    const phoneForType = getPhoneByType(newType, contact);
    setPhone(phoneForType);
    // Clear phone error when switching types
    setPhoneError(null);
  };

  // Helper function to check if there are any changes
  const hasChanges = (
    currentEmail: string,
    currentPhone: string,
    currentPhoneType: "mobile" | "home" | "work",
    formData: FormData
  ): boolean => {
    if (!initialState) return true; // If no initial state, assume there are changes

    // Check email changes
    if (currentEmail !== initialState.email) {
      return true;
    }

    // Check phone number changes for the currently selected type
    const formattedPhone = currentPhone.replace(/\D/g, "");
    const initialPhoneByType = 
      currentPhoneType === "mobile" ? initialState.mobilePhone :
      currentPhoneType === "home" ? initialState.homePhone :
      initialState.workPhone;
    
    if (formattedPhone !== initialPhoneByType.replace(/\D/g, "")) {
      return true;
    }

    // Check communication preferences changes
    const newCommPrefs = {
      announcementEmail: formData.get("announcement_email") === "on",
      announcementPhone: formData.get("announcement_phone") === "on",
      announcementSms: formData.get("announcement_sms") === "on",
      appointmentEmail: formData.get("appointment_email") === "on",
      appointmentPhone: formData.get("appointment_phone") === "on",
      appointmentSms: formData.get("appointment_sms") === "on",
      billingEmail: formData.get("billing_email") === "on",
      billingPhone: formData.get("billing_phone") === "on",
      billingSms: formData.get("billing_sms") === "on",
      labEmail: formData.get("lab_email") === "on",
      labPhone: formData.get("lab_phone") === "on",
      labSms: formData.get("lab_sms") === "on",
    };

    const initialCommPrefs = initialState.communicationPreference || {};
    if (JSON.stringify(newCommPrefs) !== JSON.stringify(initialCommPrefs)) {
      return true;
    }

    return false;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightSubmitRef.current) return;
    inFlightSubmitRef.current = true;
    try {

    const emailValidationError = validateEmail(email);
    const phoneValidationError = validatePhone(phone);

    setEmailError(emailValidationError);
    setPhoneError(phoneValidationError);

    if (emailValidationError || phoneValidationError) {
      return;
    }

    const formData = new FormData(e.currentTarget);

    const communicationPrefs = {
      announcementEmail: formData.get("announcement_email") === "on",
      announcementPhone: formData.get("announcement_phone") === "on",
      announcementSms: formData.get("announcement_sms") === "on",
      appointmentEmail: formData.get("appointment_email") === "on",
      appointmentPhone: formData.get("appointment_phone") === "on",
      appointmentSms: formData.get("appointment_sms") === "on",
      billingEmail: formData.get("billing_email") === "on",
      billingPhone: formData.get("billing_phone") === "on",
      billingSms: formData.get("billing_sms") === "on",
      labEmail: formData.get("lab_email") === "on",
      labPhone: formData.get("lab_phone") === "on",
      labSms: formData.get("lab_sms") === "on",
    };

    // Check if there are any changes before calling the API
    if (!hasChanges(email, phone, phoneType, formData)) {
      // No changes detected, just navigate to next page
      navigate(addressPath);
      return;
    }

    const formattedPhone = phone.replace(/\D/g, "");

    // Build contactInfo object with the phone number in the correct field based on phoneType
    // Preserve existing phone numbers for other types
    const contactInfo: {
      email?: string;
      mobilePhone?: string;
      homePhone?: string;
      workPhone?: string;
    } = {
      email: email || undefined,
      // Preserve existing phone numbers for types we're not editing
      mobilePhone: contact?.mobilePhone || undefined,
      homePhone: contact?.homePhone || undefined,
      workPhone: contact?.workPhone || undefined,
    };

    // Update the phone number in the correct field based on selected type
    if (formattedPhone) {
      switch (phoneType) {
        case "mobile":
          contactInfo.mobilePhone = formattedPhone;
          break;
        case "home":
          contactInfo.homePhone = formattedPhone;
          break;
        case "work":
          contactInfo.workPhone = formattedPhone;
          break;
      }
    }

    const payload = {
      email: email || undefined,
      contactInfo,
      communicationPreference: communicationPrefs,
    };

    setSubmitting(true);
    setLocalError(null);
    try {
      await withLoader("Saving...", async () => {
        await updateDemographics(payload);
        // Refetch workflow so the progress bar reflects backend truth
        // (step statuses, newly-completed steps) before we navigate.
        await refreshWorkflow();
        if (isAllStepsCompleted()) {
          navigate(`/appointments/${appointmentId}/complete`);
        } else {
          const nextStep = getNextStepInSequence(location.pathname);
          if (nextStep) {
            navigate(nextStep.stepUrl);
          } else {
            navigate(addressPath);
          }
        }
      });
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
    } finally {
      inFlightSubmitRef.current = false;
    }
  };

  const personalPath = useAppointmentStepPath("personal-information");
  const addressPath = useAppointmentStepPath("address-and-identity");
  const contactPath = useAppointmentStepPath("contact-and-communication");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveAndExit = async () => {
    const emailValidationError = validateEmail(email);
    const phoneValidationError = validatePhone(phone);

    setEmailError(emailValidationError);
    setPhoneError(phoneValidationError);

    if (emailValidationError || phoneValidationError) {
      return;
    }

    // For Save & Exit, check if email or phone changed
    // (We don't check comm prefs since Save & Exit sets them all to false)
    if (initialState) {
      const formattedPhone = phone.replace(/\D/g, "");
      const initialPhoneByType = 
        phoneType === "mobile" ? initialState.mobilePhone :
        phoneType === "home" ? initialState.homePhone :
        initialState.workPhone;
      
      const hasEmailOrPhoneChanges = 
        email !== initialState.email ||
        formattedPhone !== initialPhoneByType.replace(/\D/g, "");

      if (!hasEmailOrPhoneChanges) {
        // No changes detected, navigate to next step in sequence or completion
        if (isAllStepsCompleted()) {
          navigate(`/appointments/${appointmentId}/complete`);
        } else {
          const nextStep = getNextStepInSequence(location.pathname);
          if (nextStep) {
            navigate(nextStep.stepUrl);
          } else {
            navigate(contactPath.replace("contact-and-communication", "payments"));
          }
        }
        return;
      }
    }

    const formattedPhone = phone.replace(/\D/g, "");

    // Build contactInfo object with the phone number in the correct field based on phoneType
    // Preserve existing phone numbers for other types
    const contactInfo: {
      email?: string;
      mobilePhone?: string;
      homePhone?: string;
      workPhone?: string;
    } = {
      email: email || undefined,
      // Preserve existing phone numbers for types we're not editing
      mobilePhone: contact?.mobilePhone || undefined,
      homePhone: contact?.homePhone || undefined,
      workPhone: contact?.workPhone || undefined,
    };

    // Update the phone number in the correct field based on selected type
    if (formattedPhone) {
      switch (phoneType) {
        case "mobile":
          contactInfo.mobilePhone = formattedPhone;
          break;
        case "home":
          contactInfo.homePhone = formattedPhone;
          break;
        case "work":
          contactInfo.workPhone = formattedPhone;
          break;
      }
    }

    const payload = {
      email: email || undefined,
      contactInfo,
      communicationPreference: {
        announcementEmail: false,
        announcementPhone: false,
        announcementSms: false,
        appointmentEmail: false,
        appointmentPhone: false,
        appointmentSms: false,
        billingEmail: false,
        billingPhone: false,
        billingSms: false,
        labEmail: false,
        labPhone: false,
        labSms: false,
      },
    };

    setSubmitting(true);
    setLocalError(null);
    try {
      await updateDemographics(payload);
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(contactPath.replace("contact-and-communication", "payments"));
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CheckinLayout
      title="Contact & Communication"
      subtitle="How can we best reach you?"
    >
        {(error || localError) && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert
              type="error"
              onClose={() => {
                setLocalError(null);
              }}
            >
              {error || localError}
            </Alert>
          </div>
        )}
        <form
          className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2 w-full">
              <label className="font-semibold text-[#1C1C1C] text-base">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                placeholder="user@example.com"
                required
                value={email}
                onChange={handleEmailChange}
                className={`rounded-md border py-3 px-4 w-full placeholder-[#818BA0] text-base focus:outline-none focus:border-[#01545D] ${emailError ? 'border-red-500' : 'border-[#DCE1E8]'}`}
              />
              {emailError && <div className="text-sm text-red-600">{emailError}</div>}
            </div>
            <div className="flex flex-col gap-2 w-full">
              <label className="font-semibold text-[#1C1C1C] text-base">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <div className="min-w-[110px] max-w-[140px] flex-1">
                  <FormSelect
                    label=""
                    name="phone_type"
                    options={phoneTypeOptions}
                    required
                    className="w-full"
                    value={phoneType}
                    onChange={handlePhoneTypeChange}
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="4155550067"
                    required
                    value={phone}
                    onChange={handlePhoneChange}
                    className={`rounded-md border py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:outline-none focus:border-[#01545D] w-full ${phoneError ? 'border-red-500' : 'border-[#DCE1E8]'}`}
                  />
                  {phoneError && <div className="text-sm text-red-600">{phoneError}</div>}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="text-lg font-semibold text-[#1C1C1C]">
              Communication Preferences
            </div>
            <div className="text-sm text-[#5D6A85] mb-2">
              Select how you'd like to receive different types of communications
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border border-[#DCE1E8] rounded-lg overflow-hidden">
                <thead className="bg-[#F7F8FD] border-b border-[#BEC5D2]">
                  <tr>
                    <th className="text-left px-6 py-4 font-semibold text-[#1C1C1C] text-base">
                      Category
                    </th>
                    {tableColumns.map((col) => (
                      <th
                        key={col}
                        className="text-center px-6 py-4 font-semibold text-[#1C1C1C] text-base"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#DCE1E8]">
                    <td className="px-6 py-4 text-[#1C1C1C] text-base font-normal">
                      Appointments
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="appointment_email"
                        defaultChecked={commPrefs?.appointmentEmail}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="appointment_phone"
                        defaultChecked={commPrefs?.appointmentPhone}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="appointment_sms"
                        defaultChecked={commPrefs?.appointmentSms}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                  </tr>
                  <tr className="border-b border-[#DCE1E8]">
                    <td className="px-6 py-4 text-[#1C1C1C] text-base font-normal">
                      Billing
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="billing_email"
                        defaultChecked={commPrefs?.billingEmail}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="billing_phone"
                        defaultChecked={commPrefs?.billingPhone}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="billing_sms"
                        defaultChecked={commPrefs?.billingSms}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                  </tr>
                  <tr className="border-b border-[#DCE1E8]">
                    <td className="px-6 py-4 text-[#1C1C1C] text-base font-normal">
                      Labs
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="lab_email"
                        defaultChecked={commPrefs?.labEmail}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="lab_phone"
                        defaultChecked={commPrefs?.labPhone}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="lab_sms"
                        defaultChecked={commPrefs?.labSms}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                  </tr>
                  <tr className="last:border-0">
                    <td className="px-6 py-4 text-[#1C1C1C] text-base font-normal">
                      Announcements
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="announcement_email"
                        defaultChecked={commPrefs?.announcementEmail}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="announcement_phone"
                        defaultChecked={commPrefs?.announcementPhone}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        name="announcement_sms"
                        defaultChecked={commPrefs?.announcementSms}
                        className="accent-[#01545D] w-5 h-5 rounded border border-[#BEC5D2]"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex gap-5 w-full mt-8">
            <AppButton
              variant="secondary"
              type="button"
              onClick={() => {
                const previousStep = getPreviousStep(location.pathname);
                if (previousStep) {
                  navigate(previousStep.stepUrl);
                } else {
                  navigate(personalPath);
                }
              }}
              disabled={!!workflowError || submitting || loading}
              className="flex-1"
            >
              Back
            </AppButton>
            {/* <AppButton
              variant="secondary"
              type="button"
              onClick={handleSaveAndExit}
              disabled={submitting || loading}
              className="flex-1"
            >
              {submitting ? "Saving..." : "Save & Exit"}
            </AppButton> */}
            <AppButton type="submit" disabled={!!workflowError || submitting || loading} className="flex-1">
              {submitting ? "Saving..." : "Continue"}
            </AppButton>
          </div>
        </form>
    </CheckinLayout>
  );
};

export default ContactAndCommunication;
